/**
 * GoalEditTray — full edit experience for an existing Mini-Project
 * Mission goal (DEV-1075 Phase 7.3a).
 *
 * Renders inside the shared <Tray> component (DEV-816 side panel)
 * rather than inline below the card. Pattern matches the role-assignee
 * edit tray and other "focused configuration" surfaces: keeps the
 * Goals section static so the user can still see neighboring goals
 * while configuring one. 560px-wide tray slides in from the inline
 * edge, with scrim behind. Esc + scrim click + close-X all close.
 *
 * Replaces the inline EditGoalForm that lived in GoalsSection.tsx
 * pre-Phase-7.3a. Form grew past inline-component size when the four
 * new fields landed (TipTap description editor + photo strip +
 * include-in-rollup + deadline picker on top of name/target/unit/
 * required) — pushing existing goal cards around to make room was
 * the wrong shape for configuration UI.
 *
 * Sectioned per the locked 2026-05-24 mockup, reordered 2026-05-26
 * (DEV-1109) into a config-first flow — the user picks the goal type
 * and measurement basics before naming/describing it, then sets how
 * the goal contributes to project status:
 *   - Measurement: type + target / currency / unit + (checklist items)
 *   - Identity:    name + description editor + photo strip
 *   - Scoring & Deadline: required + include-in-rollup + deadline
 *     (collapses to just "Scoring" for checklist primitive — no deadline)
 *
 * Why the reorder (DEV-1109): Photos sat between Name and the
 * measurement config in the original order, pushing the actual goal
 * configuration (type/target/unit) below a heavy visual. Required also
 * sat inside the 2-column Measurement grid as a checkbox-among-inputs,
 * which read awkwardly and conceptually didn't belong with measurement
 * mechanics. Promoting Required to a full-width toggleRow in the
 * scoring section unifies it with Included-in-rollup (same visual
 * pattern, same conceptual group: how the goal contributes to project
 * status).
 *
 * Writes through updateKpiConfig (Phase 5.4 controller wiring).
 * Soft-deletes through deactivateKpiConfig (is_active=false).
 *
 * Scope note: this tray handles the EDIT flow only. The AddGoal flow
 * stays inline in GoalsSection with the legacy minimal field set
 * (name / type / target / unit / required) for Phase 7.3a. Reaching
 * the new fields requires creating the goal first, then opening edit.
 * Filed as Task #187 for a follow-up that brings AddGoal into the
 * same tray.
 */

import React, { useCallback, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { createKpiConfig, updateKpiConfig, deactivateKpiConfig } from "../../api/kpiConfigs";
import type { MiniProjectGoalView } from "../../Data/MiniProjectConfig/miniProjectGoalView";
import {
  MP_PRIMITIVE_TO_MISSION_TYPE,
  type MpGoalPrimitive,
} from "../../Data/MiniProjectConfig/goalPrimitiveMapping";
import StatusPhotoStrip, { type StatusPhotoItem } from "../StatusPhotoStrip/StatusPhotoStrip";
import { Tray } from "../Tray/Tray";
import GoalDescriptionEditor from "./GoalDescriptionEditor";
import GoalChecklistEditor, { type ChecklistItem } from "./GoalChecklistEditor";
import styles from "./GoalEditTray.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

/**
 * Tray serves both create and edit flows so users see one consistent
 * configuration surface (Skip 2026-05-24). Mode-specific differences:
 *
 *   - Primitive: select in 'create' (sets the mission_type triple);
 *     read-only static label in 'edit' (primitive locks at creation
 *     — changing it would invalidate stored values).
 *   - Currency code: input in 'create' when primitive='currency';
 *     hidden in 'edit' (code locks at creation).
 *   - Delete button: shown in 'edit' only.
 *   - Save button label + tray title: per-mode.
 *   - Save handler: createKpiConfig in 'create', updateKpiConfig in 'edit'.
 */
type Props =
  | {
      mode: "edit";
      open: boolean;
      goal: MiniProjectGoalView | null;
      onClose: () => void;
      onMutated: () => void;
    }
  | {
      mode: "create";
      open: boolean;
      miniProjectGuid: string;
      /** Auto-positioned after existing goals (max + 1). */
      existingMaxPosition: number;
      onClose: () => void;
      onMutated: () => void;
    };

/* ── Helpers ───────────────────────────────────────────────────── */

/** Parse the JSON-stored checklist_items column into the editor's shape
 *  (DEV-1105). Defensive on bad data — empty array on parse failure.
 *  Backfills `position` from array index when the legacy shape (label_key
 *  only) is present, so old rows edited through this surface graduate to
 *  the explicit-position shape on next save. */
function parseStoredChecklistItems(raw: string | null): ChecklistItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((it) => it && typeof it === "object" && typeof it.label_key === "string")
      .map((it, idx) => ({
        label_key: String(it.label_key),
        position: typeof it.position === "number" ? it.position : idx,
      }));
  } catch {
    return [];
  }
}

/** Parse the JSON-stored photos column into the shape StatusPhotoStrip
 *  expects. Defensive on bad data — empty array on parse failure. */
function parseStoredPhotos(raw: string | null): StatusPhotoItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((p) => p && typeof p === "object" && typeof p.storageKey === "string")
      .map((p, idx) => ({
        storageKey: String(p.storageKey),
        caption: p.caption ?? null,
        position: typeof p.position === "number" ? p.position : idx,
      }));
  } catch {
    return [];
  }
}

/** Tabler-style icons used in the tray chrome. Inline so the tray
 *  has no external icon-pack dependency. */
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const XIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ── Component ─────────────────────────────────────────────────── */

const GoalEditTray: React.FC<Props> = (props) => {
  const { mode, open, onClose, onMutated } = props;
  const { t } = useAppTranslation("pages");

  // Mode-discriminated value extraction. Edit derives initial state
  // from the goal prop; Create starts from blank defaults. The parent
  // keys this component on goalGuid (edit) or "create-<bool>" (create)
  // so useState initializers fire fresh on each open.
  const goal = mode === "edit" ? props.goal : null;
  const miniProjectGuid =
    mode === "edit"
      ? (props.goal?.miniProjectGuid ?? "")
      : props.miniProjectGuid;

  // ── Local state (one slot per editable field) ──
  const [nameKey, setNameKey] = useState(goal?.nameKey || "");
  const [primitive, setPrimitive] = useState<MpGoalPrimitive>(
    (goal?.primitive ?? "count") as MpGoalPrimitive,
  );
  const [descriptionRich, setDescriptionRich] = useState<string>(goal?.descriptionRich || "");
  const [photos, setPhotos] = useState<StatusPhotoItem[]>(
    () => parseStoredPhotos(goal?.descriptionPhotos ?? null),
  );
  const [targetValue, setTargetValue] = useState(
    goal?.targetValue != null ? String(goal.targetValue) : "",
  );
  // DEV-1105 — checklist items editor state. Lives alongside target
  // because the two are exclusive: checklist primitives don't carry a
  // numeric target. See file-level comment in GoalChecklistEditor for
  // the position-as-identity invariant.
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>(
    () => parseStoredChecklistItems(goal?.checklistItems ?? null),
  );
  const [unitKey, setUnitKey] = useState(goal?.unitKey || "");
  /** Currency code: editable in create when primitive='currency';
   *  locked in edit (the underlying uom column carries the code,
   *  set at creation time only). */
  const [currencyCode, setCurrencyCode] = useState(goal?.currencyCode || "USD");
  const [required, setRequired] = useState(!!goal?.required);
  const [includedInRollup, setIncludedInRollup] = useState(goal?.includedInRollup ?? true);
  const [deadlineDate, setDeadlineDate] = useState<string>(goal?.deadlineDate || "");
  /** Drives the "+ Add deadline" → expanded picker toggle. Defaults to
   *  expanded when the goal already has a deadline. */
  const [deadlineExpanded, setDeadlineExpanded] = useState<boolean>(!!goal?.deadlineDate);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const primitiveSpec = MP_PRIMITIVE_TO_MISSION_TYPE[primitive];

  const photosForStrip = useMemo(() => photos, [photos]);

  /** Serialize the photos array for description_photos JSONB storage.
   *  Empty array → null so the server stores null rather than '[]'. */
  const serializePhotos = useCallback(
    (arr: StatusPhotoItem[]): string | undefined => {
      if (!arr || arr.length === 0) return undefined;
      return JSON.stringify(
        arr.map((p, idx) => ({
          storageKey: p.storageKey,
          caption: p.caption ?? null,
          position: typeof p.position === "number" ? p.position : idx,
        })),
      );
    },
    [],
  );

  // ── Save (branches on mode) ──
  const handleSave = useCallback(async () => {
    if (!nameKey.trim()) {
      setError(t("miniProject.sections.error.nameRequired"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const deadlineForApi = deadlineExpanded && deadlineDate ? deadlineDate : "";
      const photosSerialized = serializePhotos(photos) ?? "";

      // DEV-1105 — serialize checklist items for persistence. Only
      // populated when the active primitive is checklist; otherwise
      // we send undefined so the column stays NULL (or untouched on
      // edit). milestone_total mirrors items.length so the scoring
      // engine knows the denominator without a separate read.
      const activePrimitive =
        mode === "create" ? primitive : (goal?.primitive ?? "count");
      const isChecklist = activePrimitive === "checklist";
      const itemsSerialized = isChecklist
        ? JSON.stringify(
            checklistItems
              .filter((it) => it.label_key.trim().length > 0)
              .map((it) => ({ label_key: it.label_key.trim(), position: it.position })),
          )
        : undefined;
      const milestoneTotalForApi = isChecklist ? checklistItems.length : undefined;

      if (mode === "create") {
        // DEV-1075 Phase 7.3a — POST to unified kpi_config endpoint.
        // Mission-type triple derived from the chosen primitive via the
        // MP_PRIMITIVE_TO_MISSION_TYPE bridge.
        const uomValue =
          primitive === "currency"
            ? currencyCode.trim().toUpperCase()
            : (unitKey.trim() || primitiveSpec.defaultUom || undefined);
        await createKpiConfig({
          miniProjectGuid,
          instanceGuid: crypto.randomUUID(),
          catalogKpiGuid: "kpi_mission_outcome_v1",
          category: "mission",
          displayName: nameKey.trim(),
          weight: 1.0,
          direction: "increase_is_good",
          valueType: primitiveSpec.valueType,
          trackingMode: "cumulative",
          missionTypeKey: primitiveSpec.missionTypeKey,
          uom: uomValue,
          // Target: checklist has no target_goal semantics (DEV-1105).
          targetGoal: !isChecklist && targetValue ? Number(targetValue) : undefined,
          milestoneTotal: milestoneTotalForApi,
          checklistItems: itemsSerialized,
          position: props.mode === "create" ? props.existingMaxPosition + 1 : 0,
          scoringMode: "qualitative",
          includedInRollup,
          colorMode: "score",
          isMandatory: required,
          // DEV-1075 Phase 7.3a — new fields exposed at create time too.
          descriptionRich: descriptionRich || undefined,
          descriptionPhotos: photosSerialized || undefined,
          // Deadline: checklist has no deadline (DEV-1105).
          deadlineDate: !isChecklist && deadlineForApi ? deadlineForApi : undefined,
        });
      } else if (goal) {
        // Edit path — patch existing kpi_config row.
        await updateKpiConfig(goal.kpiConfigId, {
          displayName: nameKey.trim(),
          // Target: checklist has no target_goal semantics (DEV-1105).
          targetGoal: !isChecklist && targetValue ? Number(targetValue) : undefined,
          uom: goal.primitive === "currency" ? undefined : (unitKey.trim() || undefined),
          isMandatory: required,
          descriptionRich: descriptionRich || "",
          descriptionPhotos: photosSerialized,
          includedInRollup,
          // Deadline: checklist has no deadline (DEV-1105). Sending ""
          // signals the controller to null the column.
          deadlineDate: isChecklist ? "" : deadlineForApi,
          // DEV-1105 — push items array on edit too. milestone_total
          // mirrors length so server can score without a separate read.
          checklistItems: itemsSerialized,
          milestoneTotal: milestoneTotalForApi,
        });
      }
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [
    mode,
    props,
    miniProjectGuid,
    goal,
    nameKey,
    primitive,
    primitiveSpec,
    targetValue,
    unitKey,
    currencyCode,
    required,
    descriptionRich,
    photos,
    includedInRollup,
    deadlineDate,
    deadlineExpanded,
    checklistItems,
    onMutated,
    serializePhotos,
    t,
  ]);

  // ── Delete (soft delete via deactivate) ──
  const handleDelete = useCallback(async () => {
    if (!goal) return;
    if (!window.confirm(t("miniProject.sections.goals.confirmDelete", { name: goal.nameKey }))) return;
    setBusy(true);
    setError(null);
    try {
      await deactivateKpiConfig(goal.kpiConfigId);
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [goal, onMutated, t]);

  // ── Render ──
  // In edit mode the body is gated on goal != null (the prior goal
  // momentarily lingers while the Tray animates closed). In create
  // mode the body always renders when open.
  const bodyOk = mode === "create" || goal != null;
  const trayTitle =
    mode === "create"
      ? t("miniProject.cards.goal.createTrayTitle")
      : goal
        ? t("miniProject.cards.goal.editTrayTitle", { name: goal.nameKey })
        : t("miniProject.cards.goal.editTitle");

  return (
    <Tray open={open} onClose={onClose} title={trayTitle}>
      {bodyOk && (
        <>
          <Tray.Body className={styles.body}>

            {/* ── Measurement (DEV-1109: now leads the form) ──────── */}
            <div className={styles.sectionLabel}>
              {t("miniProject.cards.goal.section.measurement")}
            </div>

            <div className={styles.gridTwo}>
              <div className={styles.row}>
                <label className={styles.label}>
                  {t("miniProject.sections.formLabel.type")}
                </label>
                {mode === "create" ? (
                  // Create mode: user picks the primitive, which locks the
                  // mission_type triple at creation.
                  <select
                    className={styles.input}
                    value={primitive}
                    onChange={(e) => setPrimitive(e.target.value as MpGoalPrimitive)}
                    disabled={busy}
                  >
                    <option value="count">{t("miniProject.sections.goals.type.count")}</option>
                    <option value="percentage">{t("miniProject.sections.goals.type.percentage")}</option>
                    <option value="checklist">{t("miniProject.sections.goals.type.checklist")}</option>
                    <option value="currency">{t("miniProject.sections.goals.type.currency")}</option>
                  </select>
                ) : (
                  // Edit mode: read-only static label. Primitive locks at creation
                  // — switching post-creation would invalidate stored values.
                  <div className={styles.staticField}>
                    {goal && t(`miniProject.sections.goals.type.${goal.primitive}`)}
                  </div>
                )}
              </div>
              {/* Target field: hidden for checklist primitive (DEV-1105).
                  For checklist, completion is items_completed / items_total —
                  target_value has no coherent meaning. Replaced by the items
                  editor below. */}
              {primitive !== "checklist" && (
                <div className={styles.row}>
                  <label className={styles.label}>
                    {t("miniProject.sections.formLabel.target")}
                  </label>
                  <input
                    className={styles.input}
                    type="number"
                    value={targetValue}
                    onChange={(e) => setTargetValue(e.target.value)}
                    placeholder={t("miniProject.sections.goals.targetPlaceholder")}
                    disabled={busy}
                  />
                </div>
              )}
              {/* Currency code: editable in create when primitive='currency';
                  hidden in edit (locked at creation). */}
              {mode === "create" && primitive === "currency" && (
                <div className={styles.row}>
                  <label className={styles.label}>
                    {t("miniProject.sections.formLabel.currency")}
                  </label>
                  <input
                    className={styles.input}
                    value={currencyCode}
                    onChange={(e) => setCurrencyCode(e.target.value)}
                    maxLength={3}
                    disabled={busy}
                  />
                </div>
              )}
              {/* Unit field: shown for non-checklist + non-currency primitives.
                  In edit mode we use the locked goal.primitive; in create mode
                  the active primitive state drives the gate. */}
              {((mode === "edit" && goal && goal.primitive !== "checklist" && goal.primitive !== "currency")
                || (mode === "create" && primitive !== "checklist" && primitive !== "currency")) && (
                <div className={styles.row}>
                  <label className={styles.label}>
                    {t("miniProject.sections.formLabel.unit")}
                  </label>
                  <input
                    className={styles.input}
                    value={unitKey}
                    onChange={(e) => setUnitKey(e.target.value)}
                    placeholder={t("miniProject.sections.goals.unitPlaceholder")}
                    disabled={busy}
                  />
                </div>
              )}
              {/* Required moved out of Measurement (DEV-1109). It now
                  lives in the Scoring & Deadline section below as a
                  full-width toggleRow, grouped with Included-in-rollup. */}
            </div>

            {/* DEV-1105 — checklist items editor. Rendered for checklist
                primitive only (in both create and edit modes). Sits below
                the Measurement gridTwo because items can be long and the
                editor benefits from full tray width. */}
            {((mode === "create" && primitive === "checklist")
              || (mode === "edit" && goal && goal.primitive === "checklist")) && (
              <div className={styles.row}>
                <label className={styles.label}>
                  {t("miniProject.cards.goal.checklist.itemsLabel")}
                </label>
                <GoalChecklistEditor
                  items={checklistItems}
                  onChange={setChecklistItems}
                  disabled={busy}
                />
              </div>
            )}

            {/* ── Identity (DEV-1109: moved after Measurement) ────── */}
            <div className={styles.sectionLabel}>
              {t("miniProject.cards.goal.section.identity")}
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.name")}
              </label>
              <input
                className={styles.input}
                value={nameKey}
                onChange={(e) => setNameKey(e.target.value)}
                disabled={busy}
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.description")}
                <span className={styles.opt}>{t("miniProject.sections.formLabel.optional")}</span>
              </label>
              <GoalDescriptionEditor
                value={descriptionRich}
                onChange={setDescriptionRich}
                disabled={busy}
              />
              <div className={styles.hint}>
                {t("miniProject.cards.goal.descriptionHint")}
              </div>
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.photos")}
                <span className={styles.opt}>
                  {t("miniProject.cards.goal.photosLimit", { max: 6 })}
                </span>
              </label>
              <StatusPhotoStrip
                mode="write"
                photos={photosForStrip}
                onChange={setPhotos}
                maxPhotos={6}
                uploadBucket="miniproject-goal-photos"
                uploadFilePrefix="goal-photo"
              />
            </div>

            {/* ── Scoring (+ deadline for non-checklist) ────────────
                DEV-1109: Required, Included-in-rollup, and Deadline
                live here — all three describe how this goal contributes
                to project status. Required + Included use matching
                toggleRow styling (was an awkward checkbox in the
                Measurement grid before DEV-1109).
                DEV-1105: deadline scrapped for checklist primitive
                (Skip 2026-05-22). Section label adapts. */}
            {(() => {
              // Use the active primitive in create mode, the locked goal
              // primitive in edit mode. Both paths agree on hiding the
              // deadline UI when checklist is selected.
              const activePrimitive = mode === "create" ? primitive : (goal?.primitive ?? "count");
              const showDeadline = activePrimitive !== "checklist";
              return (
                <>
                  <div className={styles.sectionLabel}>
                    {showDeadline
                      ? t("miniProject.cards.goal.section.scoringAndDeadline")
                      : t("miniProject.cards.goal.section.scoring")}
                  </div>

                  <div className={styles.toggleRow}>
                    <input
                      type="checkbox"
                      checked={required}
                      onChange={(e) => setRequired(e.target.checked)}
                      disabled={busy}
                    />
                    <div className={styles.toggleText}>
                      <div className={styles.toggleLabel}>
                        {t("miniProject.sections.formLabel.required")}
                      </div>
                      <div className={styles.toggleHint}>
                        {t("miniProject.cards.goal.requiredHint")}
                      </div>
                    </div>
                  </div>

                  <div className={styles.toggleRow}>
                    <input
                      type="checkbox"
                      checked={includedInRollup}
                      onChange={(e) => setIncludedInRollup(e.target.checked)}
                      disabled={busy}
                    />
                    <div className={styles.toggleText}>
                      <div className={styles.toggleLabel}>
                        {t("miniProject.cards.goal.includedInRollup.label")}
                      </div>
                      <div className={styles.toggleHint}>
                        {t("miniProject.cards.goal.includedInRollup.hint")}
                      </div>
                    </div>
                  </div>

                  {showDeadline && (
                    <div className={styles.row}>
                      <label className={styles.label}>
                        {t("miniProject.sections.formLabel.deadline")}
                        <span className={styles.opt}>{t("miniProject.sections.formLabel.optional")}</span>
                      </label>
                      {!deadlineExpanded ? (
                        <button
                          type="button"
                          className={styles.addDeadlineLink}
                          onClick={() => setDeadlineExpanded(true)}
                          disabled={busy}
                        >
                          <PlusIcon /> {t("miniProject.cards.goal.addDeadline")}
                        </button>
                      ) : (
                        <div className={styles.deadlineBox}>
                          <input
                            className={styles.input}
                            type="date"
                            value={deadlineDate}
                            onChange={(e) => setDeadlineDate(e.target.value)}
                            disabled={busy}
                          />
                          <button
                            type="button"
                            className={styles.removeLink}
                            onClick={() => {
                              setDeadlineDate("");
                              setDeadlineExpanded(false);
                            }}
                            disabled={busy}
                            aria-label={t("miniProject.cards.goal.removeDeadline")}
                          >
                            <XIcon /> {t("miniProject.cards.goal.removeDeadline")}
                          </button>
                        </div>
                      )}
                      {deadlineExpanded && (
                        <div className={styles.hint}>
                          {t("miniProject.cards.goal.deadlineHint")}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {error && <div className={styles.errorMsg}>{error}</div>}
          </Tray.Body>

          {/* ── Actions ───────────────────────────────────────────── */}
          <Tray.Footer className={styles.footer}>
            {/* Delete: edit mode only. */}
            {mode === "edit" && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={handleDelete}
                disabled={busy}
              >
                <TrashIcon /> {t("miniProject.sections.button.delete")}
              </button>
            )}
            <button
              type="button"
              className={styles.btn}
              onClick={onClose}
              disabled={busy}
              style={mode === "create" ? { marginInlineStart: "auto" } : undefined}
            >
              {t("miniProject.sections.button.cancel")}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSave}
              disabled={busy}
            >
              {busy
                ? t("miniProject.sections.button.saving")
                : mode === "create"
                  ? t("miniProject.sections.goals.button.add")
                  : t("miniProject.sections.button.save")}
            </button>
          </Tray.Footer>
        </>
      )}
    </Tray>
  );
};

export default GoalEditTray;
